import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Book, PenTool, Building2, Settings, ChevronDown, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Role Switcher component for multi-role users
 * Displays in navigation bar when user has 2+ identity roles
 * Allows switching between roles without re-authentication
 * 
 * Note: Backend sends "author", frontend normalizes to "creator"
 */

// Role display configuration
// "author" maps to same config as "creator" - they are equivalent
const ROLE_CONFIG = {
  reader: {
    label: 'Reader',
    icon: Book,
    route: '/home',
    color: 'text-blue-400'
  },
  creator: {
    label: 'Author',  // Display "Author", not "creator"
    icon: PenTool,
    route: '/author',
    color: 'text-emerald-400'
  },
  author: {
    label: 'Author',  // Same as creator - backend sends "author"
    icon: PenTool,
    route: '/author',
    color: 'text-emerald-400'
  },
  publisher: {
    label: 'Publisher',
    icon: Building2,
    route: '/publisher',
    color: 'text-purple-400'
  },
  admin: {
    label: 'Admin',
    icon: Settings,
    route: '/admin',
    color: 'text-yellow-400'
  }
};

// Identity roles (not modifier roles like beta_tester, promotional)
// Both "author" and "creator" are valid identity roles
const IDENTITY_ROLES = ['reader', 'creator', 'author', 'publisher', 'admin'];

// Normalize role - treat "author" as "creator" for frontend consistency
const normalizeRole = (role) => {
  if (role === 'author') return 'creator';
  return role;
};

const RoleSwitcher = ({ className = '' }) => {
  const navigate = useNavigate();
  const { user, activeRole, setActiveRole } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Extract and normalize identity roles from user's roles
  // "author" → "creator" for frontend consistency
  const userRoles = user?.roles || [];
  const normalizedRoles = userRoles.map(normalizeRole);
  const identityRoles = [...new Set(normalizedRoles.filter(role => 
    ['reader', 'creator', 'publisher', 'admin'].includes(role)
  ))];
  
  // Normalize active role for display
  const normalizedActiveRole = normalizeRole(activeRole);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Don't render if:
  // 1. User has less than 2 identity roles (after normalization)
  // 2. User is an admin (admins always stay in admin mode)
  if (!user || identityRoles.length < 2 || normalizedRoles.includes('admin')) {
    return null;
  }

  const currentRoleConfig = ROLE_CONFIG[normalizedActiveRole] || ROLE_CONFIG.reader;
  const CurrentIcon = currentRoleConfig.icon;
  
  // Check if user has a saved role preference
  const savedPreference = localStorage.getItem('preferredRole');
  const hasSavedPreference = savedPreference !== null;

  const handleRoleSwitch = (newRole) => {
    if (newRole !== normalizedActiveRole) {
      setActiveRole(newRole);
      const targetRoute = ROLE_CONFIG[newRole]?.route || '/home';
      navigate(targetRoute);
    }
    setIsOpen(false);
  };

  const handleClearPreference = () => {
    localStorage.removeItem('preferredRole');
    setIsOpen(false);
  };

  const otherRoles = identityRoles.filter(role => role !== normalizedActiveRole);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-800/60 hover:bg-slate-700/80 border border-slate-600/50 hover:border-slate-500/60 transition-all text-sm"
        data-testid="role-switcher-trigger"
      >
        <CurrentIcon className={`w-4 h-4 ${currentRoleConfig.color}`} />
        <span className="text-slate-200 font-medium">{currentRoleConfig.label}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-52 py-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50">
          <div className="px-3 py-2 text-xs text-slate-500 uppercase tracking-wider border-b border-slate-700">
            Switch Role
          </div>
          
          {/* Current role (with check mark) */}
          <button
            className="w-full px-3 py-2 flex items-center gap-3 text-left hover:bg-slate-700/50 text-slate-200"
            onClick={() => setIsOpen(false)}
          >
            <CurrentIcon className={`w-4 h-4 ${currentRoleConfig.color}`} />
            <span className="flex-1">{currentRoleConfig.label}</span>
            <Check className="w-4 h-4 text-green-400" />
          </button>

          {/* Other available roles */}
          {otherRoles.map(role => {
            const config = ROLE_CONFIG[role];
            if (!config) return null;
            const Icon = config.icon;
            
            return (
              <button
                key={role}
                onClick={() => handleRoleSwitch(role)}
                className="w-full px-3 py-2 flex items-center gap-3 text-left hover:bg-slate-700/50 text-slate-300 hover:text-white transition-colors"
                data-testid={`role-switch-to-${role}`}
              >
                <Icon className={`w-4 h-4 ${config.color}`} />
                <span>Switch to {config.label}</span>
              </button>
            );
          })}

          {/* Clear saved preference option */}
          {hasSavedPreference && (
            <>
              <div className="border-t border-slate-700 my-1" />
              <button
                onClick={handleClearPreference}
                className="w-full px-3 py-2 flex items-center gap-3 text-left hover:bg-slate-700/50 text-slate-400 hover:text-slate-200 transition-colors text-sm"
                data-testid="clear-role-preference"
              >
                <Settings className="w-4 h-4" />
                <span>Ask me each login</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default RoleSwitcher;
